import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Dimensions,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { ProgressBar } from 'react-native-paper';
import { LineChart } from 'react-native-chart-kit';

import type { AnonymizedBenchmark } from '../../store/slices/socialLearningAnalyticsSlice';

interface PeerBenchmarkCardProps {
  benchmark: AnonymizedBenchmark;
  onViewDetails: () => void;
}

const { width: screenWidth } = Dimensions.get('window');

export const PeerBenchmarkCard: React.FC<PeerBenchmarkCardProps> = ({
  benchmark,
  onViewDetails,
}) => {
  const getPercentileColor = (percentile: number) => {
    if (percentile >= 90) return '#4CAF50';
    if (percentile >= 75) return '#8BC34A';
    if (percentile >= 50) return '#FF9800';
    if (percentile >= 25) return '#FF5722';
    return '#F44336';
  };

  const getPercentileLabel = (percentile: number) => {
    if (percentile >= 90) return 'Excellent';
    if (percentile >= 75) return 'Above Average';
    if (percentile >= 50) return 'Average';
    if (percentile >= 25) return 'Below Average';
    return 'Needs Improvement';
  };

  const chartData = {
    labels: ['You', 'Avg', 'Top 10%'],
    datasets: [
      {
        data: [
          benchmark.userScore,
          benchmark.averageScore,
          benchmark.topPercentileScore,
        ],
        color: (opacity = 1) => `rgba(33, 150, 243, ${opacity})`,
        strokeWidth: 2,
      },
    ],
  };

  const chartConfig = {
    backgroundColor: '#ffffff',
    backgroundGradientFrom: '#ffffff',
    backgroundGradientTo: '#ffffff',
    decimalPlaces: 1,
    color: (opacity = 1) => `rgba(33, 150, 243, ${opacity})`,
    labelColor: (opacity = 1) => `rgba(0, 0, 0, ${opacity})`,
    style: {
      borderRadius: 16,
    },
    propsForDots: {
      r: '4',
      strokeWidth: '2',
      stroke: '#2196F3',
    },
  };

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.titleContainer}>
          <Text style={styles.skillArea}>{benchmark.skillArea}</Text>
          <View style={styles.percentileContainer}>
            <Text
              style={[
                styles.percentileText,
                { color: getPercentileColor(benchmark.userPercentile) },
              ]}
            >
              {benchmark.userPercentile}th percentile
            </Text>
            <Text style={styles.percentileLabel}>
              {getPercentileLabel(benchmark.userPercentile)}
            </Text>
          </View>
        </View>
        <TouchableOpacity style={styles.detailsButton} onPress={onViewDetails}>
          <Ionicons name='chevron-forward' size={20} color='#666' />
        </TouchableOpacity>
      </View>

      {/* Progress Visualization */}
      <View style={styles.progressSection}>
        <View style={styles.progressHeader}>
          <Text style={styles.progressLabel}>Your Progress</Text>
          <Text style={styles.progressValue}>
            {benchmark.userScore.toFixed(1)} /{' '}
            {benchmark.topPercentileScore.toFixed(1)}
          </Text>
        </View>
        <ProgressBar
          progress={benchmark.userScore / benchmark.topPercentileScore}
          color={getPercentileColor(benchmark.userPercentile)}
          style={styles.progressBar}
        />
        <View style={styles.progressMarkers}>
          <View style={styles.marker}>
            <Text style={styles.markerLabel}>Average</Text>
            <Text style={styles.markerValue}>
              {benchmark.averageScore.toFixed(1)}
            </Text>
          </View>
          <View style={styles.marker}>
            <Text style={styles.markerLabel}>Top 10%</Text>
            <Text style={styles.markerValue}>
              {benchmark.topPercentileScore.toFixed(1)}
            </Text>
          </View>
        </View>
      </View>

      {/* Comparison Chart */}
      <View style={styles.chartSection}>
        <Text style={styles.chartTitle}>Performance Comparison</Text>
        <LineChart
          data={chartData}
          width={screenWidth - 64}
          height={120}
          chartConfig={chartConfig}
          bezier
          style={styles.chart}
        />
      </View>

      {/* Improvement Suggestions */}
      {benchmark.improvementSuggestions.length > 0 && (
        <View style={styles.suggestionsSection}>
          <Text style={styles.suggestionsTitle}>Improvement Suggestions</Text>
          {benchmark.improvementSuggestions
            .slice(0, 2)
            .map((suggestion, index) => (
              <View key={index} style={styles.suggestionItem}>
                <Ionicons name='bulb-outline' size={16} color='#FF9800' />
                <Text style={styles.suggestionText}>{suggestion}</Text>
              </View>
            ))}
        </View>
      )}

      {/* Confidence Interval */}
      <View style={styles.confidenceSection}>
        <View style={styles.confidenceHeader}>
          <Ionicons name='information-circle-outline' size={16} color='#666' />
          <Text style={styles.confidenceText}>
            Confidence: {Math.round(benchmark.confidenceInterval[0])}% -{' '}
            {Math.round(benchmark.confidenceInterval[1])}%
          </Text>
        </View>
        <Text style={styles.sampleSizeText}>
          Based on {benchmark.sampleSize} peer comparisons
        </Text>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 16,
  },
  titleContainer: {
    flex: 1,
  },
  skillArea: {
    fontSize: 18,
    fontWeight: '700',
    color: '#333',
    marginBottom: 4,
  },
  percentileContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  percentileText: {
    fontSize: 14,
    fontWeight: '600',
  },
  percentileLabel: {
    fontSize: 12,
    color: '#666',
    backgroundColor: '#f5f5f5',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
  },
  detailsButton: {
    padding: 4,
  },
  progressSection: {
    marginBottom: 16,
  },
  progressHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  progressLabel: {
    fontSize: 14,
    color: '#666',
  },
  progressValue: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
  },
  progressBar: {
    height: 8,
    borderRadius: 4,
    backgroundColor: '#f0f0f0',
  },
  progressMarkers: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 8,
  },
  marker: {
    alignItems: 'center',
  },
  markerLabel: {
    fontSize: 10,
    color: '#666',
  },
  markerValue: {
    fontSize: 12,
    fontWeight: '600',
    color: '#333',
    marginTop: 2,
  },
  chartSection: {
    marginBottom: 16,
  },
  chartTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
    marginBottom: 8,
  },
  chart: {
    borderRadius: 8,
  },
  suggestionsSection: {
    marginBottom: 12,
  },
  suggestionsTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
    marginBottom: 8,
  },
  suggestionItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 6,
  },
  suggestionText: {
    fontSize: 13,
    color: '#666',
    marginLeft: 8,
    flex: 1,
    lineHeight: 18,
  },
  confidenceSection: {
    borderTopWidth: 1,
    borderTopColor: '#f0f0f0',
    paddingTop: 12,
  },
  confidenceHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  confidenceText: {
    fontSize: 12,
    color: '#666',
    marginLeft: 6,
  },
  sampleSizeText: {
    fontSize: 11,
    color: '#999',
  },
});
